import Vue from 'vue'
import Axios from 'axios'
import VueAxios from 'vue-axios'
import { BootstrapVue, BootstrapVueIcons } from 'bootstrap-vue'
import App from './App'
import router, { refreshPermissions } from './router' // ⬅ importa o hook
import store from './vuex/store'
import CxltToastr from 'cxlt-vue2-toastr'
import VueTheMask from 'vue-the-mask'
import 'cxlt-vue2-toastr/dist/css/cxlt-vue2-toastr.css'
import parametros from '../static/parametros/parametros.js'
import VueAutonumeric from 'vue-autonumeric'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css'

Vue.component('Multiselect', Multiselect)

Vue.use(BootstrapVue)
Vue.use(BootstrapVueIcons)
Vue.use(VueAxios, Axios)
Vue.use(VueTheMask)
Vue.use(VueAutonumeric)
Vue.use(CxltToastr, { position: 'top right', timeOut: '5000' })

Vue.axios.defaults.baseURL = parametros.configapp.baseURL

// ---------- Helpers ----------
const isRefreshEndpoint = (url = '') =>
  url.endsWith('/api/refreshtoken') || url.includes('/api/refreshtoken')

const getTokens = () => ({
  token: localStorage.getItem('token'),
  tokenRefresh: localStorage.getItem('tokenRefresh'),
})

const setTokens = ({ token, tokenRefresh }) => {
  if (token) localStorage.setItem('token', token)
  if (tokenRefresh) localStorage.setItem('tokenRefresh', tokenRefresh)
}

const clearTokens = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('tokenRefresh')
}

const doLogout = () => {
  clearTokens()
  // opcional: limpar estados do store ligados ao usuário
  // store.commit('usuario/CLEAR') // se existir
  router.replace('/pages/login')
}

function getRefreshToken() {
  // O request interceptor vai injetar o Authorization com tokenRefresh
  return Vue.axios.post('/api/refreshtoken', localStorage.getItem('tokenRefresh'))
}

// ---------- Request Interceptor ----------
Vue.axios.interceptors.request.use(
  (config) => {
    const { token, tokenRefresh } = getTokens()

    // Sem token e fora do login: manda pro login (sem reload)
    if (router.currentRoute.path !== '/pages/login' && !token && !isRefreshEndpoint(config.url)) {
      doLogout()
      // cancelar esta request
      return Promise.reject({ __canceled: true })
    }

    // Define Authorization
    if (isRefreshEndpoint(config.url)) {
      if (tokenRefresh) config.headers['Authorization'] = `Bearer ${tokenRefresh}`
    } else if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }

    return config
  },
  (error) => Promise.reject(error)
)

// ---------- Response Interceptor ----------
Vue.axios.interceptors.response.use(
  (response) => {
    // (Opcional) se o backend sinalizar alteração de permissões, recarrega cache
    // Ex.: cabeçalho 'x-permissions-updated: 1'
    if (response?.headers && response.headers['x-permissions-updated'] === '1') {
      refreshPermissions().catch(() => {})
    }
    return response
  },
  async (err) => {
    // Pode ser erro sem response (rede, cancelamento, etc.)
    const status = err?.response?.status
    const url = err?.config?.url || ''

    // Cancelamento deliberado (sem token) — já redirecionado
    if (err && err.__canceled) return Promise.reject(err)

    // 401 no endpoint de refresh => sessão inválida
    if (status === 401 && isRefreshEndpoint(url)) {
      doLogout()
      return Promise.reject(err)
    }

    // 401 em qualquer outra rota => tenta um refresh (apenas uma vez)
    if (status === 401 && err.config && !err.config.__isRetryRequest) {
      try {
        const resp = await getRefreshToken()
        setTokens({
          token: resp.data?.token,
          tokenRefresh: resp.data?.tokenRefresh,
        })
        err.config.__isRetryRequest = true
        // Re-dispara a requisição original com os novos tokens
        return Vue.axios(err.config)
      } catch (e) {
        doLogout()
        return Promise.reject(e)
      }
    }

    // 404
    if (status === 404 && !err.config?.noToast) {
      const novoVue = new Vue()
      novoVue.$bvToast.toast('Ocorreu uma Falha: O Recurso solicitado não foi encontrado', {
        id: 'meutoast',
        title: 'Recurso Inválido',
        autoHideDelay: 3000,
        variant: 'danger',
        appendToast: true
      })
      return Promise.reject(err)
    }

    // 400 / 409 / 422 — repassa
    if (status === 400 || status === 409 || status === 422) {
      return Promise.reject(err)
    }

    // 500
    if (status === 500 && !err.config?.noToast) {
      const novoVue = new Vue()
      novoVue.$bvToast.toast('Ocorreu uma Falha: ' + (err.response?.statusText || 'Erro no servidor'), {
        id: 'meutoast',
        title: 'Falha no Servidor',
        autoHideDelay: 3000,
        variant: 'danger',
        appendToast: true
      })
      return Promise.reject(err)
    }

    // Qualquer outro erro: loga e repassa
    console.log('erro não utilizado', err)
    return Promise.reject(err)
  }
)

// ---------- Mixin (inalterado, só removi reload) ----------
Vue.mixin({
  methods: {
    $voltarComFallback(rotaPadrao = '/dashboard/painelgerencial') {
      const ultimaRota = localStorage.getItem('ultimaRota')
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else if (ultimaRota && ultimaRota !== this.$route.path) {
        this.$router.push(ultimaRota)
      } else {
        this.$router.push(rotaPadrao)
      }
    },
    async $baixarRelatorioEPular(urlRelatorio, nomeArquivo = 'relatorio.pdf', tituloJanela = 'Relatório') {
      try {
        const response = await this.$http.get(urlRelatorio, { responseType: 'arraybuffer' })
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', nomeArquivo)
        const janela = window.open(link.href)
        if (janela) {
          janela.onload = function () {
            setTimeout(() => { janela.document.title = tituloJanela }, 3000)
          }
        }
        this.$voltarComFallback()
      } catch (erro) {
        this.$voltarComFallback()
        this.$store.commit('controle/setAguardando', false)
        this.toastCount++
        this.$root.$bvToast.toast(`Falha da Geração do Relatório! ${erro}`, {
          title: 'Aviso',
          autoHideDelay: 3000,
          variant: 'danger',
          appendToast: true
        })
      }
    }
  }
})

/* eslint-disable no-new */
new Vue({
  el: '#app',
  router,
  store,
  template: '<App/>',
  components: { App }
})



// WEBPACK FOOTER //
// ./src/main.js